import React, { useEffect, useRef, useState } from 'react';
import cambioEstadoService from '../../services/cambioEstadoService';
import { obtenerSiguienteEstadoART } from '../../services/art/artEstadoService';
import { actualizarComentariosTramite } from '../../services/tramiteService';
import { toast } from 'react-toastify';
import { obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo, subirArchivo } from '../../services/archivoService';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SaveIcon from '@mui/icons-material/Save';
import BiotechIcon from '@mui/icons-material/Biotech';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// Sugerencias para el dropdown de estudios comunes
const ESTUDIOS_COMUNES = [
    'RMN — Resonancia Magnética Nuclear',
    'TAC — Tomografía Computada',
    'Ecografía',
    'Radiografía simple',
    'Análisis de laboratorio',
    'Electrocardiograma (ECG)',
    'Electromiograma (EMG)',
    'Espirometría',
    'Interconsulta traumatológica',
    'Interconsulta oftalmológica',
    'Interconsulta neurológica',
    'Interconsulta psicológica / psiquiátrica',
    'Otro...',
];

// ── Persistencia ────────────────────────────────────────────────────────────
const PREFIJO = '[ESPERA-DOC:';

const parsearNotaGuardada = (comentarios = '') => {
    const texto = String(comentarios || '').trim();
    if (!texto.startsWith(PREFIJO)) return null;
    try {
        const modoMatch = texto.match(/\[ESPERA-DOC:(ESTUDIOS_MEDICOS|OTROS)\]/);
        if (!modoMatch) return null;
        const modo = modoMatch[1];

        // Observaciones / Descripción (última línea con ese label)
        const obsMatch = texto.match(/(?:Observaciones|Descripci[oó]n): ([\s\S]*?)(?:\n\[|$)/);
        const observaciones = obsMatch ? obsMatch[1].trim() : '';

        if (modo === 'ESTUDIOS_MEDICOS') {
            // Estudios: "nombre|true, nombre|false, ..."
            const estudiosMatch = texto.match(/Estudios: ([^\n]+)/);
            const gestionMatch = texto.match(/Gestiona: ([^\n]+)/);
            const estudios = estudiosMatch
                ? estudiosMatch[1].split(';;').map((parte, i) => {
                    const [nombre, recibido] = parte.split('|');
                    return { id: `restored_${i}`, nombre: nombre.trim(), recibido: recibido === 'true' };
                })
                : [];
            const quienGestiona = gestionMatch
                ? (gestionMatch[1].trim() === 'Nosotros' ? 'NOSOTROS' : 'CLIENTE')
                : 'NOSOTROS';
            return { modo, estudios, quienGestiona, observaciones };
        }
        return { modo: 'OTROS', estudios: [], quienGestiona: 'NOSOTROS', observaciones };
    } catch {
        return null;
    }
};

const formatearNota = ({ modo, estudios, quienGestiona, observaciones }) => {
    if (modo === 'ESTUDIOS_MEDICOS') {
        const estudiosStr = estudios.map(e => `${e.nombre}|${e.recibido}`).join(';;');
        const gestionLabel = quienGestiona === 'NOSOTROS' ? 'Nosotros' : 'El cliente';
        let nota = `[ESPERA-DOC:ESTUDIOS_MEDICOS]\nEstudios: ${estudiosStr || '(ninguno)'}\nGestiona: ${gestionLabel}`;
        if (observaciones.trim()) nota += `\nObservaciones: ${observaciones.trim()}`;
        return nota;
    }
    let nota = '[ESPERA-DOC:OTROS]';
    if (observaciones.trim()) nota += `\nDescripción: ${observaciones.trim()}`;
    return nota;
};

const formatearComentarioFinal = ({ modo, estudios, quienGestiona, observaciones }) => {
    if (modo === 'ESTUDIOS_MEDICOS') {
        const recibidos = estudios.filter(e => e.recibido).map(e => e.nombre);
        const pendientes = estudios.filter(e => !e.recibido).map(e => e.nombre);
        const gestionLabel = quienGestiona === 'NOSOTROS' ? 'Nosotros' : 'El cliente';
        let comentario = `Documentación validada (Estudios médicos). Gestionados por: ${gestionLabel}.`;
        if (recibidos.length > 0) comentario += ` Recibidos: ${recibidos.join(', ')}.`;
        if (pendientes.length > 0) comentario += ` Pendientes: ${pendientes.join(', ')}.`;
        if (observaciones.trim()) comentario += ` Obs: ${observaciones.trim()}`;
        return comentario;
    }
    const base = 'Documentación validada (Otros).';
    return observaciones.trim() ? `${base} ${observaciones.trim()}` : base;
};

// ── Componente ───────────────────────────────────────────────────────────────
function GestorEsperaDocumentacion({ tramite, onAvanzar }) {
    const [modo, setModo] = useState(null); // 'ESTUDIOS_MEDICOS' | 'OTROS'

    // Lista dinámica de estudios: { id, nombre, recibido }
    const [estudios, setEstudios] = useState([]);
    const [quienGestiona, setQuienGestiona] = useState('NOSOTROS');
    const [observaciones, setObservaciones] = useState('');

    // Inputs para agregar nuevo estudio
    const [estudioSeleccionado, setEstudioSeleccionado] = useState('');
    const [estudioPersonalizado, setEstudioPersonalizado] = useState('');
    const mostrarInputPersonalizado = estudioSeleccionado === 'Otro...';

    // Archivos
    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // Acciones
    const [guardandoNota, setGuardandoNota] = useState(false);
    const [avanzando, setAvanzando] = useState(false);

    useEffect(() => {
        const notaGuardada = parsearNotaGuardada(tramite.comentarios);
        if (notaGuardada) {
            setModo(notaGuardada.modo);
            setEstudios(notaGuardada.estudios || []);
            setQuienGestiona(notaGuardada.quienGestiona || 'NOSOTROS');
            setObservaciones(notaGuardada.observaciones || '');
        }
        cargarArchivos();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            setArchivos(archivosBackend.map(a => ({
                id: `backend_${a.idArchivo}`,
                idArchivo: a.idArchivo,
                nombre: a.nombre,
                tamaño: (a.tamaño / (1024 * 1024)).toFixed(2) + ' MB',
                estado: 'subido',
                esDelBackend: true,
            })));
        } catch {
            toast.error('No se pudieron cargar los archivos adjuntos.');
        } finally {
            setCargandoArchivos(false);
        }
    };

    // ── Gestión de estudios ──────────────────────────────────────────────────

    const agregarEstudio = () => {
        const nombre = mostrarInputPersonalizado
            ? estudioPersonalizado.trim()
            : estudioSeleccionado.trim();

        if (!nombre || nombre === 'Otro...') {
            toast.warn('Ingresá el nombre del estudio.');
            return;
        }
        if (estudios.some(e => e.nombre.toLowerCase() === nombre.toLowerCase())) {
            toast.warn('Ese estudio ya está en la lista.');
            return;
        }

        setEstudios(prev => [...prev, { id: `est_${Date.now()}`, nombre, recibido: false }]);
        setEstudioSeleccionado('');
        setEstudioPersonalizado('');
    };

    const toggleRecibido = (id) => {
        setEstudios(prev => prev.map(e => e.id === id ? { ...e, recibido: !e.recibido } : e));
    };

    const eliminarEstudio = (id) => {
        setEstudios(prev => prev.filter(e => e.id !== id));
    };

    // ── Archivos ─────────────────────────────────────────────────────────────

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(e.type === 'dragenter' || e.type === 'dragover');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.[0]) handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
    };

    const handleSeleccionarArchivos = (e) => {
        const nuevos = Array.from(e.target.files).map((file, idx) => ({
            id: `local_${Date.now()}_${idx}`,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            archivo: file,
            estado: 'pendiente',
            esDelBackend: false,
        }));
        setArchivos(prev => [...prev, ...nuevos]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubirArchivos = async () => {
        const pendientes = archivos.filter(a => a.estado === 'pendiente');
        if (!pendientes.length) { toast.warn('No hay archivos pendientes'); return; }
        setSubiendoArchivos(true);
        for (const a of pendientes) {
            try {
                await subirArchivo(tramite.idTramite, a.archivo);
                setArchivos(prev => prev.map(x => x.id === a.id ? { ...x, estado: 'subido' } : x));
            } catch {
                setArchivos(prev => prev.map(x => x.id === a.id ? { ...x, estado: 'error' } : x));
            }
        }
        toast.success('Archivos procesados');
        setSubiendoArchivos(false);
    };

    const handleDescargarArchivo = async (a) => {
        try { await descargarArchivo(a.idArchivo, a.nombre); }
        catch { toast.error('Error al descargar'); }
    };

    const handleEliminarArchivo = async (id) => {
        const a = archivos.find(x => x.id === id);
        if (!a) return;
        if (a.esDelBackend) {
            try {
                await eliminarArchivo(a.idArchivo);
                setArchivos(prev => prev.filter(x => x.id !== id));
                toast.success('Archivo eliminado');
            } catch { toast.error('Error al eliminar'); }
        } else {
            setArchivos(prev => prev.filter(x => x.id !== id));
        }
    };

    // ── Guardar nota sin avanzar ─────────────────────────────────────────────

    const handleGuardarNota = async () => {
        if (!modo) { toast.warn('Seleccioná el tipo de documentación primero.'); return; }
        setGuardandoNota(true);
        try {
            const nota = formatearNota({ modo, estudios, quienGestiona, observaciones });
            await actualizarComentariosTramite(tramite.idTramite, nota);
            toast.success('Progreso guardado correctamente.');
        } catch {
            toast.error('Error al guardar el progreso.');
        } finally {
            setGuardandoNota(false);
        }
    };

    // ── Validar y avanzar ────────────────────────────────────────────────────

    const handleValidar = async () => {
        if (archivos.some(a => a.estado === 'pendiente')) {
            toast.error('Subí todos los archivos pendientes antes de avanzar.');
            return;
        }
        if (!modo) {
            toast.error('Seleccioná el tipo de documentación antes de avanzar.');
            return;
        }
        setAvanzando(true);
        try {
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual);
            const comentarioFinal = formatearComentarioFinal({ modo, estudios, quienGestiona, observaciones });
            await cambioEstadoService.cambiarEstado(tramite.idTramite, {
                idEstado: siguienteEstado.id,
                comentario: comentarioFinal,
            });
            toast.success('Documentación validada. Avanzando al siguiente estado.');
            onAvanzar();
        } catch (error) {
            toast.error('Error al avanzar: ' + error.message);
        } finally {
            setAvanzando(false);
        }
    };

    // ── Derived ──────────────────────────────────────────────────────────────

    const hayArchivosPendientes = archivos.some(a => a.estado === 'pendiente');
    const recibidosCount = estudios.filter(e => e.recibido).length;
    const totalEstudios = estudios.length;

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">

            {/* Encabezado */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Espera de Documentación</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">Pendiente</span>
            </div>

            {/* Selector de tipo */}
            <div className="mb-4">
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                    ¿Qué tipo de documentación se está esperando?
                </p>
                <div className="d-flex gap-3 flex-wrap">
                    {[
                        { key: 'ESTUDIOS_MEDICOS', icon: <BiotechIcon fontSize="small" className="me-2" />, label: 'Estudios médicos' },
                        { key: 'OTROS',            icon: <FolderOpenIcon fontSize="small" className="me-2" />, label: 'Otros documentos' },
                    ].map(({ key, icon, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`btn d-flex align-items-center rounded-pill px-4 py-2 ${modo === key ? 'btn-info text-white' : 'btn-outline-secondary'}`}
                            onClick={() => { setModo(key); if (key !== modo) { setEstudios([]); setObservaciones(''); } }}
                        >
                            {icon}{label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── ESTUDIOS MÉDICOS ── */}
            {modo === 'ESTUDIOS_MEDICOS' && (
                <div className="mb-4">

                    {/* Progreso */}
                    {totalEstudios > 0 && (
                        <div className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3 bg-light border">
                            <div style={{ flex: 1 }}>
                                <div className="d-flex justify-content-between mb-1">
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Estudios recibidos</span>
                                    <span style={{ fontSize: '0.85rem', color: recibidosCount === totalEstudios ? '#2e7d32' : '#777' }}>
                                        {recibidosCount} / {totalEstudios}
                                    </span>
                                </div>
                                <div className="progress" style={{ height: '6px' }}>
                                    <div
                                        className="progress-bar bg-success"
                                        style={{ width: `${totalEstudios > 0 ? (recibidosCount / totalEstudios) * 100 : 0}%`, transition: 'width 0.3s' }}
                                    />
                                </div>
                            </div>
                            {recibidosCount === totalEstudios && totalEstudios > 0 && (
                                <span className="text-success fw-semibold" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                    ✓ Todos recibidos
                                </span>
                            )}
                        </div>
                    )}

                    {/* Lista de estudios */}
                    {estudios.length > 0 && (
                        <div className="mb-3">
                            {estudios.map(estudio => (
                                <div
                                    key={estudio.id}
                                    className={`d-flex align-items-center gap-2 p-2 mb-1 rounded-3 border ${estudio.recibido ? 'border-success bg-success bg-opacity-10' : 'border-light bg-white'}`}
                                >
                                    {/* Checkbox de recibido */}
                                    <button
                                        type="button"
                                        className="btn btn-sm p-0 border-0 bg-transparent"
                                        onClick={() => toggleRecibido(estudio.id)}
                                        title={estudio.recibido ? 'Marcar como pendiente' : 'Marcar como recibido'}
                                        style={{ lineHeight: 1 }}
                                    >
                                        {estudio.recibido
                                            ? <CheckCircleIcon style={{ color: '#2e7d32', fontSize: '1.4rem' }} />
                                            : <RadioButtonUncheckedIcon style={{ color: '#aaa', fontSize: '1.4rem' }} />}
                                    </button>

                                    {/* Nombre */}
                                    <span
                                        style={{
                                            flex: 1,
                                            fontSize: '0.9rem',
                                            textDecoration: estudio.recibido ? 'none' : 'none',
                                            color: estudio.recibido ? '#2e7d32' : '#333',
                                            fontWeight: estudio.recibido ? 600 : 400,
                                        }}
                                    >
                                        {estudio.nombre}
                                        {estudio.recibido && (
                                            <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>Recibido</span>
                                        )}
                                    </span>

                                    {/* Eliminar */}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger border-0 p-1"
                                        onClick={() => eliminarEstudio(estudio.id)}
                                        title="Quitar de la lista"
                                    >
                                        <DeleteIcon style={{ fontSize: '1rem' }} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Agregar estudio */}
                    <div className="p-3 border rounded-3 bg-light">
                        <p className="mb-2 fw-semibold" style={{ fontSize: '0.85rem', color: '#555' }}>
                            <AddCircleOutlineIcon fontSize="small" className="me-1" />
                            Agregar estudio solicitado
                        </p>
                        <div className="d-flex gap-2 flex-wrap">
                            <select
                                className="form-select form-select-sm"
                                style={{ flex: '1 1 220px' }}
                                value={estudioSeleccionado}
                                onChange={e => { setEstudioSeleccionado(e.target.value); setEstudioPersonalizado(''); }}
                            >
                                <option value="">— Seleccioná un estudio —</option>
                                {ESTUDIOS_COMUNES.map(e => (
                                    <option key={e} value={e}>{e}</option>
                                ))}
                            </select>

                            {mostrarInputPersonalizado && (
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ flex: '1 1 180px' }}
                                    placeholder="Nombre del estudio..."
                                    value={estudioPersonalizado}
                                    onChange={e => setEstudioPersonalizado(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') agregarEstudio(); }}
                                    autoFocus
                                />
                            )}

                            <button
                                type="button"
                                className="btn btn-sm btn-primary rounded-pill px-3"
                                onClick={agregarEstudio}
                                disabled={!estudioSeleccionado}
                            >
                                <AddCircleOutlineIcon fontSize="small" className="me-1" />
                                Agregar
                            </button>
                        </div>
                    </div>

                    {/* ¿Quién gestiona? */}
                    <div className="mt-3">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.88rem' }}>
                            ¿Quién gestiona los turnos/estudios?
                        </label>
                        <div className="d-flex gap-4">
                            {[
                                { val: 'NOSOTROS', label: 'Nosotros los gestionamos' },
                                { val: 'CLIENTE',  label: 'El cliente los trae' },
                            ].map(({ val, label }) => (
                                <div key={val} className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        id={`gestiona_${val}`}
                                        checked={quienGestiona === val}
                                        onChange={() => setQuienGestiona(val)}
                                    />
                                    <label className="form-check-label" htmlFor={`gestiona_${val}`}>{label}</label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Observaciones */}
                    <div className="mt-3">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.88rem' }}>
                            Observaciones (opcional)
                        </label>
                        <textarea
                            className="form-control"
                            rows="2"
                            placeholder="Ej: Turno para RMN el 15/05. Eco pendiente de resultado."
                            value={observaciones}
                            onChange={e => setObservaciones(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* ── OTROS ── */}
            {modo === 'OTROS' && (
                <div className="mb-4 p-3 border rounded-3 bg-light">
                    <h6 className="fw-semibold text-primary mb-2">
                        <FolderOpenIcon fontSize="small" className="me-1" />
                        Descripción de la documentación pendiente
                    </h6>
                    <textarea
                        className="form-control"
                        rows="4"
                        placeholder="Ej: Falta la denuncia policial y los últimos 3 recibos de sueldo."
                        value={observaciones}
                        onChange={e => setObservaciones(e.target.value)}
                    />
                </div>
            )}

            {/* Guardar nota (sin avanzar) */}
            {modo && (
                <div className="d-flex justify-content-end mb-4">
                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-4"
                        onClick={handleGuardarNota}
                        disabled={guardandoNota}
                    >
                        <SaveIcon fontSize="small" />
                        {guardandoNota ? 'Guardando...' : 'Guardar progreso (sin avanzar)'}
                    </button>
                </div>
            )}

            <hr className="my-1" />

            {/* ── Archivos ── */}
            <div className="mt-3">
                <h6 className="fw-semibold text-primary mb-1">Archivos adjuntos</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.83rem' }}>
                    Podés ir subiendo los estudios a medida que llegan. No es necesario tenerlos todos para guardar el progreso.
                </p>

                {cargandoArchivos ? (
                    <p className="text-muted">Cargando archivos...</p>
                ) : (
                    <>
                        {archivos.length > 0 && (
                            <div className="list-group mb-3">
                                {archivos.map(a => (
                                    <div key={a.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                        <div>
                                            <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                            <span className="fw-semibold">{a.nombre}</span>
                                            <small className="text-muted ms-2">({a.tamaño})</small>
                                            <span className={`badge ms-2 ${a.estado === 'subido' ? 'bg-success' : a.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                {a.estado === 'subido' ? 'subido ✓' : a.estado}
                                            </span>
                                        </div>
                                        <div className="d-flex gap-1">
                                            {a.esDelBackend && (
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleDescargarArchivo(a)} title="Descargar">
                                                    <DownloadIcon fontSize="small" />
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(a.id)} title="Eliminar">
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={`border rounded-3 p-4 text-center ${dragActive ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed'}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            style={{ cursor: 'pointer' }}
                        >
                            <input type="file" multiple ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                            <CloudUploadIcon className="text-muted mb-2" style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto' }} />
                            <p className="mb-0 text-muted">Arrastrá archivos aquí o hacé clic para seleccionar</p>
                        </div>

                        {hayArchivosPendientes && (
                            <div className="d-flex justify-content-center mt-3">
                                <button
                                    className="btn btn-primary rounded-pill px-4"
                                    onClick={handleSubirArchivos}
                                    disabled={subiendoArchivos}
                                >
                                    {subiendoArchivos
                                        ? 'Subiendo...'
                                        : `Subir ${archivos.filter(a => a.estado === 'pendiente').length} archivo(s)`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Validar y avanzar */}
            <div className="d-flex flex-column align-items-center mt-4 pt-3 border-top gap-2">
                <button
                    className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-5 py-2 fw-semibold"
                    onClick={handleValidar}
                    disabled={avanzando || subiendoArchivos || hayArchivosPendientes || !modo}
                    title={
                        !modo ? 'Seleccioná el tipo de documentación primero'
                        : hayArchivosPendientes ? 'Subí todos los archivos pendientes primero'
                        : 'Marcar como recibida y avanzar al siguiente estado'
                    }
                >
                    <AssignmentTurnedInIcon fontSize="small" />
                    {avanzando ? 'Avanzando...' : 'Validar Documentación y Avanzar'}
                </button>
                {modo === 'ESTUDIOS_MEDICOS' && totalEstudios > 0 && recibidosCount < totalEstudios && (
                    <small className="text-muted">
                        Podés avanzar aunque no todos los estudios estén marcados como recibidos.
                    </small>
                )}
                {!modo && (
                    <small className="text-muted">Seleccioná el tipo de documentación para habilitar.</small>
                )}
            </div>
        </div>
    );
}

export default GestorEsperaDocumentacion;
